import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router";
import { useHydrated } from "remix-utils/use-hydrated";

/**
 * The PLP grid's client-accumulated state (Load More appends, Start From The
 * Beginning prepends) and how it survives a round-trip through history.
 *
 * Extracted from `shop.$categoryId.tsx`'s `CategoryGridBody` so the seeding
 * rule below is independently unit-testable (COM-599).
 *
 * ## Why the HYDRATION render must NOT read history state (COM-599)
 *
 * Load More / Start Over stash the accumulated grid on the history entry
 * (`navigate(url, { state: { accumulatedHits, gridStartPage } })`) so a
 * PDP round-trip restores it. React Router hydrates `location.state` from
 * `window.history.state.usr` and reports the initial document load as
 * `navigationType === "POP"` — so on a hard reload (or a non-bfcache back-nav)
 * after Load More, the client's FIRST render used to seed `useState` straight
 * from `location.state`: 72 tiles and no "Load earlier pages" button, while
 * the server (which has no history state) rendered 36 tiles + the button.
 * React 19 throws #418 at the grid boundary, discards the SSR tree and
 * re-renders it client-side — the multi-second dead-click window. Reproduced
 * on dev and on the RC build; the largest app-owned share of the #418 residual.
 *
 * Rule: on the hydration render, `hits` / `gridStartPage` are derived from the
 * loader data EXACTLY as the server derives them, and history state is applied
 * in a layout effect right after hydration. `impressionBatch` never reaches the
 * markup, so it is seeded restore-aware up front on every path — one
 * `/resources/gtm-impressions` request at the right offset, never an offset-0
 * request that a corrected one has to supersede (SFCC-12175 W4).
 *
 * ## Why ONLY the hydration render is deferred — scroll restoration
 *
 * `root.tsx` renders `{children}` first and `<ScrollRestoration />` as a LATER
 * sibling. On a client-side POP remount (PDP → browser back) React Router sets
 * `restoreScrollPosition` in the same state update that swaps the matches, and
 * `useScrollRestoration`'s layout effect (keyed on `location`) calls
 * `window.scrollTo(0, savedY)` in the SAME commit that mounts this route. A
 * `setState` from a child layout effect does not flush until every layout
 * effect of that commit has run, so a deferred restore would let the router
 * scroll against the 36-tile document (clamped) and then grow the grid to 72
 * underneath the shopper. The PLP loader awaits `data` (no defer) precisely to
 * keep the grid complete for that scrollTo — see `category-loader.server.ts`.
 *
 * So the restore is deferred ONLY when this mount IS the hydration pass.
 * `useHydrated()` (uSES-backed) is false during the hydration render and true
 * on the first render of any later client mount; captured in a ref at mount so
 * the false → true flip after hydration cannot re-seed. When it is true there
 * is no server HTML to match and the `useState` initializers read history
 * state synchronously, exactly as the pre-COM-599 code did — the accumulated
 * grid is in the first commit and the router's scrollTo sees the full height.
 *
 * On the hydration pass, React Router performs NO scroll restore at all. The
 * data router is created with `hydrationData` (HydratedRouter always passes
 * it), so `initialScrollRestored` is pre-set to true and the initial state is
 * `restoreScrollPosition: false`; `enableScrollRestoration` skips its
 * `updateState` branch and `useScrollRestoration`'s layout effect returns early
 * on `restoreScrollPosition === false`. Reload / bfcache-miss back-nav is
 * positioned by the BROWSER's native scroll restoration (the router's pagehide
 * handler sets `history.scrollRestoration = "auto"` before the entry is left),
 * and the browser sees the same single-page SSR document before and after
 * COM-599. The layout-effect restore only has to land before the first
 * hydrated paint — which a `setState` from a layout effect guarantees (it
 * flushes synchronously in the hydration commit) — so there is no 36 → 72
 * flash, exactly when the pre-COM-599 client render used to grow the grid.
 * React 19 does not warn about `useLayoutEffect` during SSR; it is skipped.
 */

export type PlpHistoryState<T> = {
  accumulatedHits?: T[];
  // The 1-indexed page number of the FIRST tile in the rendered grid.
  // `1` when the user landed on page 1 / no `?page` param OR after Start
  // From The Beginning prepended pages 1..N-1. `N>1` when the user is
  // viewing a deep window (landed on `?page=N` and hasn't run Start Over
  // yet). Drives:
  //   - `offsetStart` math (the counter's "Showing X - Y of Z")
  //   - Start From The Beginning visibility (only shows when > 1)
  //   - Load More's next-page calculation (the URL's `?page` may have
  //     drifted away from the actual grid start after Start Over)
  gridStartPage?: number;
};

/** The most recently LOADED batch and its position in the rendered grid. */
export type ImpressionBatch = { pids: string[]; offset: number };

/**
 * SFCC-12175 W4: the impression batch for `pageHits` when the rendered grid
 * is `grid`. `offset` is how many tiles precede the batch in the grid — on a
 * back-nav the grid is restored from `accumulatedHits` (potentially many
 * pages) while the loader's `pageHits` is the single page for the URL's
 * `?page=N`, so the page sits at some offset inside it. Assuming 0 made page
 * 2's tiles report positions 1..36 while the tile-click `gtmIndex` reported
 * 37..72 for those same tiles, i.e. `view_item_list` and `select_item`
 * disagreeing. Falls back to 0 when the page's first tile isn't in the grid
 * (or is at position 0) so the failure mode is "no worse than before", never
 * a wrong shift.
 */
export const batchFor = <T extends { productId?: string }>(
  pageHits: T[],
  grid: T[],
): ImpressionBatch => {
  const pids = pageHits
    .map(h => h.productId)
    .filter((id): id is string => Boolean(id));
  const at = pids.length ? grid.findIndex(h => h.productId === pids[0]) : -1;
  return { pids, offset: at > 0 ? at : 0 };
};

/**
 * The history state we restore from — only a POP entry (back/forward, or the
 * initial document load) that actually carries a non-empty accumulated grid.
 * Forward navs (PUSH/REPLACE — filter / sort / Link clicks) must NOT restore
 * even if `location.state` from a prior entry survives: that would silently
 * render the pre-filter grid.
 */
const restorableState = <T>(
  locationState: unknown,
  navigationType: string,
): (PlpHistoryState<T> & { accumulatedHits: T[] }) | null => {
  if (navigationType !== "POP") return null;
  const state = (locationState ?? null) as PlpHistoryState<T> | null;
  if (!Array.isArray(state?.accumulatedHits)) return null;
  if (state.accumulatedHits.length === 0) return null;
  return state as PlpHistoryState<T> & { accumulatedHits: T[] };
};

export function useAccumulatedGrid<
  T extends { productId?: string },
  TData extends { hits?: T[] },
>({ data, currentPage }: { data: TData; currentPage: number }) {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Is this mount the hydration pass (false) or a later client mount (true)?
  // See the module note: only the hydration render must mirror the server.
  const hydratedAtMount = useRef(useHydrated());
  // Hydration render: no restore (the initializers must match the server).
  // Client-side mount (PDP → back): restore synchronously so the grid is
  // complete in the commit `<ScrollRestoration>` scrolls in.
  const [initialRestore] = useState(() =>
    hydratedAtMount.current
      ? restorableState<T>(location.state, navigationType)
      : null,
  );

  const [hits, setHits] = useState<T[]>(
    () => initialRestore?.accumulatedHits ?? data.hits ?? [],
  );
  // SFCC-11807: the pids fed to the impression endpoint = the most recently
  // LOADED batch (page 1 on first paint; the new batch on Load More), not the
  // fixed loader `data.hits`. Load More is a fetcher-append (the loader doesn't
  // re-run), so `data.hits` never grows — feeding it would leave load-more tiles
  // unimpressed. Kept in step by the route's `handleLoadMoreResolved` + the
  // data-reset effect below.
  // SFCC-12175 W4: pids and their grid offset live in ONE state object so they
  // cannot drift. With the default RESULTS_PER_PAGE of 36 the impression
  // `index` continues across Load More (37, 38, …) and stays consistent with
  // the tile-click `gtmIndex` — both are positions within the same rendered
  // list.
  // Seeded restore-aware on EVERY path (including hydration): this value never
  // reaches the markup, so there is no mismatch risk, and seeding it at offset
  // 0 first would make `useTileImpressions` fire a wrong-offset request whose
  // corrected twin then has to supersede it (COM-599 review).
  const [impressionBatch, setImpressionBatch] = useState<ImpressionBatch>(() =>
    batchFor(
      data.hits ?? [],
      restorableState<T>(location.state, navigationType)?.accumulatedHits ??
        data.hits ??
        [],
    ),
  );
  const [gridStartPage, setGridStartPage] = useState<number>(() =>
    initialRestore ? (initialRestore.gridStartPage ?? 1) : currentPage,
  );

  // Hydration-pass-only restore of the accumulated grid (COM-599). A client
  // mount already seeded synchronously above. Runs once per mount; the ref
  // collapses StrictMode's dev double-invocation to a no-op.
  const restoredOnMountRef = useRef(false);
  useLayoutEffect(() => {
    if (restoredOnMountRef.current) return;
    restoredOnMountRef.current = true;
    if (hydratedAtMount.current) return;
    const restored = restorableState<T>(location.state, navigationType);
    if (!restored) return;
    setHits(restored.accumulatedHits);
    setGridStartPage(restored.gridStartPage ?? 1);
    // Mount-only by design: later changes are the data-reset effect's job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The loader re-runs in two scenarios we have to distinguish:
  //   A) Filter / sort / query change (forward nav, PUSH) — fresh result
  //      set, accumulation must reset to the new single-page hits. Even
  //      if `location.state` from a prior history entry survives the
  //      navigation, we must NOT restore it — that would silently render
  //      the pre-filter grid.
  //   B) Back-nav from a PDP (POP) — the same URL we left, but the loader
  //      runs again because page→pdp→page navigated away and back. The
  //      history entry we land on carries the `accumulatedHits` we stored
  //      on the way out; we must restore that grid, NOT the loader's
  //      single page.
  //
  // Distinguisher: `useNavigationType()`. POP = back/forward; PUSH/REPLACE
  // = Link click / `navigate()` / `setSearchParams()`. Filter clicks go
  // through `setSearchParams` (PUSH), so they fall into branch (A).
  //
  // The ref-equality short-circuit below is belt-and-suspenders on top of
  // the `[data, ...]` dep array — React StrictMode double-invokes effects
  // in dev, which would otherwise write `setHits` twice on every data
  // change. The check collapses the second invocation into a no-op.
  const lastDataRef = useRef(data);
  useEffect(() => {
    if (lastDataRef.current === data) return;
    lastDataRef.current = data;
    const restored = restorableState<T>(location.state, navigationType);
    if (restored) {
      // SFCC-11807 + SFCC-12175 W4: a back-nav restores an ACCUMULATED grid, so
      // the loader's single page of `data.hits` sits at some offset inside it —
      // derive that rather than resetting to 0 (see the note on the state above).
      setImpressionBatch(batchFor(data.hits ?? [], restored.accumulatedHits));
      setHits(restored.accumulatedHits);
      setGridStartPage(restored.gridStartPage ?? 1);
    } else {
      // Forward nav: the grid IS the loader's hits, so the batch starts at 0.
      setImpressionBatch({
        pids: (data.hits ?? [])
          .map(h => h.productId)
          .filter(Boolean) as string[],
        offset: 0,
      });
      // Forward nav (filter / sort / query / Link click). Reset the grid
      // to the loader's hits and re-anchor the start-of-grid pointer to
      // the new URL's `currentPage` — filter clicks reset `?page` to
      // canonical so this is usually 1.
      setHits(data.hits ?? []);
      setGridStartPage(currentPage);
    }
  }, [data, location.state, navigationType, currentPage]);

  return {
    hits,
    setHits,
    gridStartPage,
    setGridStartPage,
    impressionBatch,
    setImpressionBatch,
  };
}
